import { escapeRegex } from "./escape-regex";

/**
 * Creates a safe RegExp from user input by escaping special regex characters.
 *
 * This function prevents Regular Expression Denial of Service (ReDoS) attacks
 * by sanitizing user input before constructing a RegExp object.
 *
 * SECURITY: All special regex characters (.*+?^${}()|[]\) are escaped
 * to prevent catastrophic backtracking and ReDoS vulnerabilities.
 *
 * @param userInput - The user-provided string to convert to a safe regex pattern
 * @param flags - Optional regex flags (e.g., 'i' for case-insensitive, 'g' for global)
 * @returns A safe RegExp object with escaped special characters
 *
 * @example
 * // Safe: Special characters are escaped
 * const regex = createSafeRegex('user.*input', 'i');
 * // Results in: /user\.\*input/i
 *
 * @see {@link escapeRegex} for the underlying sanitization logic
 */
export function createSafeRegex(userInput: string, flags?: string): RegExp {
  // nosemgrep: javascript.lang.security.audit.detect-non-literal-regexp.detect-non-literal-regexp
  // deepcode ignore RegExpInjection: User input is sanitized via escapeRegex() utility before RegExp construction
  const safeFlags = flags?.match(/^[gimsuyd]*$/) ? flags : undefined;

  try {
    return new RegExp(escapeRegex(userInput), safeFlags);
  } catch {
    return new RegExp(escapeRegex(userInput));
  }
}
